/**
 * Busy-slot detection — mirrors iOS CityPlanningView.isSlotBusy.
 * A slot is busy when any scheduled item overlaps the slot's 2-hour window.
 */

import type { ItineraryDay, ItineraryItem } from '../types/client';
import type { PlanTimeSlot } from './aggregatedPlanData';
import { defaultStartHour } from './cityPlanningSlots';

function parseHmToMinutes(hm: string | null | undefined): number | null {
  if (!hm || !/^\d{1,2}:\d{2}/.test(hm)) return null;
  const [h, m] = hm.split(':').map((p) => Number.parseInt(p, 10));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function itemRangeMinutes(item: ItineraryItem): { start: number; end: number } | null {
  const start = parseHmToMinutes(item.time);
  if (start == null) return null;
  const duration = item.durationMinutes ?? 60;
  return { start, end: start + Math.max(duration, 1) };
}

/**
 * True when the slot's default 2-hour window overlaps any timed item on the day.
 */
export function isSlotBusy(options: {
  slot: PlanTimeSlot;
  dateYmd: string;
  cityVisitId: string;
  itineraryDays: ItineraryDay[] | null | undefined;
}): boolean {
  const slotStart = defaultStartHour(options.slot) * 60;
  const slotEnd = slotStart + 120;

  const day = (options.itineraryDays ?? []).find(
    (d) =>
      d.date === options.dateYmd &&
      (d.cityId === options.cityVisitId || !d.cityId)
  );
  if (!day?.items?.length) return false;

  for (const item of day.items) {
    const range = itemRangeMinutes(item);
    if (!range) continue;
    if (range.start < slotEnd && range.end > slotStart) {
      return true;
    }
  }
  return false;
}
